import React, { useMemo, useState } from 'react';
import { AlertTriangle, Check, X } from 'lucide-react';
import { confirmDeviceProfile } from '../api/tvApi';

/**
 * HardwareChangeCard
 *
 * Rendered inline in TVDetails whenever the selected device has a
 * `previousEdidFingerprint` subdocument — meaning the EDID has changed
 * and the change hasn't yet been silenced by the active profile's
 * hdmi_expected match. (When the new EDID matches hdmi_expected, the
 * backend silently updates the fingerprint without snapshotting the
 * old one, so this card simply never renders for expected steady state.)
 *
 * Shows:
 *   1. Before / after EDID summary (mfr + model + dims)
 *   2. Blast radius — how many of the device's active schedules still
 *      fit vs break at the new hardware dims. The numbers are computed
 *      *live* from the `blastRadius` prop the caller passes, which comes
 *      from the tv:hardware:changed socket event OR from a fresh
 *      recompute when the card is rendered from the device doc directly.
 *   3. Two primary actions:
 *         • "Keep current profile"   → POST /profile/confirm (clears prev)
 *         • "Pick a new profile"     → opens ProfilePickerModal via callback
 *      Plus a subtle dismiss X that also confirms (equivalent to Keep).
 *
 * Contract:
 *   <HardwareChangeCard
 *     device={tv}                         // must include previousEdidFingerprint + edidFingerprint
 *     blastRadius={...}                   // optional, live from socket event
 *     onOpenProfilePicker={() => ...}     // caller opens ProfilePickerModal
 *     onDeviceUpdated={(device) => ...}   // caller refreshes its local state
 *   />
 */
const HardwareChangeCard = ({ device, blastRadius, onOpenProfilePicker, onDeviceUpdated }) => {
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(null);

  const prev = device?.previousEdidFingerprint;
  const curr = device?.edidFingerprint;
  // Caller may pass blastRadius from a socket event. When not provided,
  // we synthesise a minimal structure so the card still renders — the
  // numbers section simply shows "unknown impact" in that case.
  const blast = useMemo(() => {
    if (blastRadius) return blastRadius;
    return { totalSlots: null, fits: null, broken: null, brokenList: [] };
  }, [blastRadius]);

  if (!prev?.width) return null;

  const handleKeep = async () => {
    if (!device?.deviceId) return;
    setConfirming(true);
    setError(null);
    try {
      const res = await confirmDeviceProfile(device.deviceId);
      if (typeof onDeviceUpdated === 'function') {
        try { onDeviceUpdated(res?.device || null); } catch (_) { /* noop */ }
      }
    } catch (err) {
      setError(err?.message || 'Failed to confirm profile');
    } finally {
      setConfirming(false);
    }
  };

  const describe = (fp) => {
    if (!fp?.width) return '—';
    const base = `${fp.width}×${fp.height}`;
    const who = [fp.manufacturer, fp.monitorName].filter(Boolean).join(' ');
    return who ? `${who} · ${base}` : base;
  };

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <AlertTriangle size={18} color="#b45309" />
          <div>
            <div style={styles.title}>Screen hardware changed</div>
            <div style={styles.subtitle}>
              The HDMI fingerprint for this device is different from what we saw before.
              Review and confirm the screen profile.
            </div>
          </div>
        </div>
        <button
          type="button"
          aria-label="Dismiss and keep current profile"
          onClick={handleKeep}
          disabled={confirming}
          style={styles.dismissBtn}
        >
          <X size={14} />
        </button>
      </div>

      <div style={styles.compareGrid}>
        <div style={styles.compareRow}>
          <span style={styles.compareLabel}>Before</span>
          <span style={styles.compareValue}>{describe(prev)}</span>
        </div>
        <div style={styles.compareRow}>
          <span style={styles.compareLabel}>Now</span>
          <span style={{ ...styles.compareValue, fontWeight: 700 }}>{describe(curr)}</span>
        </div>
      </div>

      {blast.totalSlots != null && blast.totalSlots > 0 && (
        <div style={styles.impactBox}>
          <div style={styles.impactTitle}>Impact on active schedules</div>
          <div style={styles.impactRow}>
            <span style={{ ...styles.impactPill, background: '#dcfce7', color: '#15803d' }}>
              ✓ {blast.fits} fit
            </span>
            {blast.broken > 0 && (
              <span style={{ ...styles.impactPill, background: '#fef2f2', color: '#b91c1c' }}>
                ✗ {blast.broken} wrong aspect
              </span>
            )}
            <span style={styles.impactTotal}>of {blast.totalSlots} total</span>
          </div>
          {blast.brokenList?.length > 0 && (
            <ul style={styles.brokenList}>
              {blast.brokenList.map((b, i) => (
                <li key={i}>
                  <code style={styles.brokenCode}>{b.contentId}</code> — {b.contentResolution}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {blast.totalSlots === 0 && (
        <div style={styles.noSchedulesHint}>
          No active schedules on this screen — you can swap freely.
        </div>
      )}

      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.actions}>
        <button
          type="button"
          onClick={onOpenProfilePicker}
          disabled={confirming}
          style={styles.primaryBtn}
        >
          Pick a new profile
        </button>
        <button
          type="button"
          onClick={handleKeep}
          disabled={confirming}
          style={styles.secondaryBtn}
        >
          <Check size={14} />
          {confirming ? 'Confirming…' : 'Keep current profile'}
        </button>
      </div>
    </div>
  );
};

const styles = {
  card: {
    border: '1px solid #fde68a',
    background: 'linear-gradient(180deg, #fffbeb, #fffdf5)',
    borderRadius: 10,
    padding: 14,
    marginBottom: 14,
    boxShadow: '0 2px 8px rgba(200, 161, 71, 0.1)',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 10,
    marginBottom: 10,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: 14,
    fontWeight: 700,
    color: '#92400e',
    marginBottom: 2,
  },
  subtitle: {
    fontSize: 12,
    color: '#78350f',
    lineHeight: 1.45,
  },
  dismissBtn: {
    padding: 4,
    border: '1px solid #fde68a',
    background: 'rgba(255, 255, 255, 0.7)',
    borderRadius: 6,
    cursor: 'pointer',
    color: '#92400e',
    display: 'flex',
    flexShrink: 0,
  },
  compareGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    padding: '10px 12px',
    background: '#fff',
    borderRadius: 8,
    border: '1px solid #fde68a',
    marginBottom: 10,
  },
  compareRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  compareLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: '#92400e',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    width: 50,
  },
  compareValue: {
    fontSize: 13,
    color: '#0f172a',
  },
  impactBox: {
    padding: '10px 12px',
    background: '#fff',
    borderRadius: 8,
    border: '1px solid #fde68a',
    marginBottom: 10,
  },
  impactTitle: {
    fontSize: 10,
    fontWeight: 700,
    color: '#92400e',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 8,
  },
  impactRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  impactPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '3px 10px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
  },
  impactTotal: {
    fontSize: 11,
    color: '#78350f',
  },
  brokenList: {
    margin: '8px 0 0',
    paddingLeft: 18,
    fontSize: 11,
    color: '#78350f',
    lineHeight: 1.6,
  },
  brokenCode: {
    background: '#fef2f2',
    color: '#b91c1c',
    padding: '1px 6px',
    borderRadius: 3,
    fontFamily: 'ui-monospace, Menlo, monospace',
    fontSize: 10,
  },
  noSchedulesHint: {
    padding: '8px 12px',
    background: '#fff',
    borderRadius: 6,
    border: '1px solid #fde68a',
    fontSize: 12,
    color: '#78350f',
    marginBottom: 10,
  },
  actions: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  primaryBtn: {
    flex: 1,
    padding: '8px 14px',
    fontSize: 13,
    fontWeight: 700,
    background: '#c8a147',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
  },
  secondaryBtn: {
    padding: '8px 14px',
    fontSize: 13,
    fontWeight: 600,
    background: '#fff',
    color: '#92400e',
    border: '1px solid #fde68a',
    borderRadius: 8,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  },
  error: {
    padding: '6px 10px',
    fontSize: 12,
    color: '#b91c1c',
    background: '#fef2f2',
    borderRadius: 6,
    marginBottom: 10,
  },
};

export default HardwareChangeCard;
